import { useState } from 'react'
import { api } from '@/api/client'
import { useFetch, useMutate } from '@/hooks/useApi'
import {
  AsyncState,
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  Icon,
  Input,
  Loading,
  Modal,
  PageHead,
  SectionHead
} from '@/components/ui'
import { SubscriptionActions } from '@/components/subscription'
import { AuditLog } from '@/components/audit-log'
import { dateOnly } from '@/lib/format'
import { useAuth } from '@/auth/AuthContext'

// Только super_admin видит этот раздел (маршрут защищён в App.tsx)

interface LicenseKey {
  id: string
  clubId: string
  key: string
  licenseExpiresAt: number
  createdAt: number
  activatedAt: number | null
  deviceId: string | null
  revoked: boolean
}

interface Device {
  id: string
  clubId: string
  name: string
  revoked: boolean
  lastSeenAt: number | null
  createdAt: number
  licenseExpiresAt: number | null
  renewalStatus: string
  renewalNote: string | null
  renewalRequestedAt: number | null
}

interface Club {
  id: string
  name: string
}

interface RenewalRequest {
  deviceId: string
  deviceName: string
  clubId: string
  clubName: string
  licenseExpiresAt: number | null
  renewalNote: string | null
  renewalRequestedAt: number | null
}

function licenseStatusLabel(expiresAt: number | null): React.JSX.Element {
  if (!expiresAt) return <span className="muted">не задана</span>
  const days = Math.floor((expiresAt - Date.now()) / 86400000)
  if (days < 0) return <span style={{ color: 'var(--red)' }}>истекла ({dateOnly(expiresAt)})</span>
  if (days <= 14)
    return (
      <span style={{ color: 'var(--amber)' }}>
        {dateOnly(expiresAt)} ({days} дн.)
      </span>
    )
  return <span style={{ color: 'var(--green)' }}>{dateOnly(expiresAt)}</span>
}

/**
 * Явный и заметный срок действия: «Действует до DD.MM.YYYY (N дн.)»,
 * «истекла DD.MM.YYYY» (красным) либо «срок не задан». Используется в сводке
 * и в колонке таблицы, чтобы было понятно, на какой срок выдана лицензия.
 */
function licenseTermLabel(expiresAt: number | null): React.JSX.Element {
  if (!expiresAt) return <span className="muted">срок не задан</span>
  const days = Math.floor((expiresAt - Date.now()) / 86400000)
  if (days < 0)
    return <strong style={{ color: 'var(--red)' }}>истекла {dateOnly(expiresAt)}</strong>
  const color = days <= 14 ? 'var(--amber)' : 'var(--green)'
  return (
    <span>
      Действует до <strong style={{ color }}>{dateOnly(expiresAt)}</strong> ({days} дн.)
    </span>
  )
}

export function Licenses(): React.JSX.Element {
  return (
    <>
      <PageHead
        eyebrow="Супер-админ"
        title="Лицензии"
        lead="Выпуск лицензионных ключей для активации терминалов, просмотр и одобрение заявок на продление."
      />
      <PendingRenewals />
      <LicensesManager />
    </>
  )
}

// ─────────────────────── Заявки на продление ───────────────────────

function PendingRenewals(): React.JSX.Element {
  const list = useFetch<RenewalRequest[]>('/v1/clubs/renewals/pending')
  const [approving, setApproving] = useState<RenewalRequest | null>(null)
  const [newExpiry, setNewExpiry] = useState('')
  const approve = useMutate()

  const rows = list.data ?? []

  const onApprove = async (): Promise<void> => {
    if (!approving) return
    const ts = new Date(newExpiry).getTime()
    if (!ts || isNaN(ts)) return
    const ok = await approve.mutate(() =>
      api.post(`/v1/clubs/${approving.clubId}/devices/${approving.deviceId}/approve-license`, {
        licenseExpiresAt: ts
      })
    )
    if (ok) {
      setApproving(null)
      setNewExpiry('')
      list.reload()
    }
  }

  if (!list.loading && rows.length === 0) return <></>

  return (
    <section style={{ marginBottom: 32 }}>
      <SectionHead title="Заявки на продление" meta={rows.length > 0 ? `${rows.length} шт.` : ''} />
      <AsyncState query={list} />
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Клуб</th>
                <th>Терминал</th>
                <th>Текущий срок</th>
                <th>Дата запроса</th>
                <th>Примечание</th>
                <th style={{ width: 120 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.deviceId}>
                  <td>{r.clubName}</td>
                  <td>{r.deviceName}</td>
                  <td>{licenseStatusLabel(r.licenseExpiresAt)}</td>
                  <td>{r.renewalRequestedAt ? dateOnly(r.renewalRequestedAt) : '—'}</td>
                  <td style={{ maxWidth: 260, wordBreak: 'break-word' }}>{r.renewalNote || '—'}</td>
                  <td>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        setApproving(r)
                        // По умолчанию +1 год от сегодня
                        const d = new Date()
                        d.setFullYear(d.getFullYear() + 1)
                        setNewExpiry(d.toISOString().slice(0, 10))
                      }}
                    >
                      Одобрить
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {approving && (
        <Modal
          title={`Продление: ${approving.deviceName}`}
          onClose={() => setApproving(null)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setApproving(null)} disabled={approve.saving}>
                Отмена
              </Button>
              <Button
                variant="primary"
                onClick={() => void onApprove()}
                disabled={approve.saving || !newExpiry}
              >
                {approve.saving && <span className="spin" />}
                Продлить
              </Button>
            </>
          }
        >
          <div className="form-grid">
            <Field label="Клуб / Терминал" full>
              <div className="input" style={{ background: 'transparent', cursor: 'default' }}>
                {approving.clubName} / {approving.deviceName}
              </div>
            </Field>
            {approving.renewalNote && (
              <Field label="Примечание от владельца" full>
                <textarea
                  className="input"
                  rows={3}
                  readOnly
                  value={approving.renewalNote}
                  style={{ resize: 'none', cursor: 'default' }}
                />
              </Field>
            )}
            <Field label="Новый срок действия лицензии" full>
              <Input
                type="date"
                value={newExpiry}
                onChange={(e) => setNewExpiry(e.target.value)}
                autoFocus
              />
            </Field>
          </div>
          {approve.error && (
            <div className="form-error" style={{ marginTop: 14 }}>
              {approve.error}
            </div>
          )}
        </Modal>
      )}
    </section>
  )
}

// ─────────────────────── Клубы + ключи ───────────────────────

function LicensesManager(): React.JSX.Element {
  const clubs = useFetch<Club[]>('/v1/clubs')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const selected = (clubs.data ?? []).find((c) => c.id === selectedId) ?? null

  return (
    <>
      <div className="table-wrap" style={{ marginBottom: 24 }}>
        <table className="rtable rtable--clickable">
          <thead>
            <tr>
              <th>Клуб</th>
            </tr>
          </thead>
          <tbody>
            {clubs.loading && (
              <tr>
                <td colSpan={1}>
                  <Loading />
                </td>
              </tr>
            )}
            {!clubs.loading && (clubs.data ?? []).length === 0 && (
              <tr>
                <td colSpan={1}>
                  <EmptyState message="Клубов нет." />
                </td>
              </tr>
            )}
            {(clubs.data ?? []).map((c) => (
              <tr
                key={c.id}
                className={c.id === selectedId ? 'is-selected' : undefined}
                onClick={() => setSelectedId((id) => (id === c.id ? null : c.id))}
              >
                <td>{c.name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && <ClubLicenseDetail key={selected.id} club={selected} />}
    </>
  )
}

function ClubLicenseDetail({ club }: { club: Club }): React.JSX.Element {
  // Ключом перерисовки дёргаем секции терминалов после продления/приостановки.
  const [rev, setRev] = useState(0)
  return (
    <div className="club-detail">
      {/* Главный блок: продление кнопками. Ключи ниже нужны только для ПЕРВОЙ
          активации терминала — продлевать через новый ключ больше не нужно. */}
      <SubscriptionActions clubId={club.id} onChanged={() => setRev((r) => r + 1)} />
      <LicenseKeysSection clubId={club.id} clubName={club.name} />
      <DevicesLicenseSection key={rev} clubId={club.id} />
      <AuditLog key={`audit-${rev}`} clubId={club.id} limit={50} />
    </div>
  )
}

// ── Лицензионные ключи клуба ──
function LicenseKeysSection({
  clubId,
  clubName
}: {
  clubId: string
  clubName: string
}): React.JSX.Element {
  const list = useFetch<LicenseKey[]>(`/v1/clubs/${clubId}/license-keys`, undefined, [clubId])
  const [creating, setCreating] = useState(false)
  const [revoking, setRevoking] = useState<LicenseKey | null>(null)
  const revoke = useMutate()
  const { claims } = useAuth()
  const isSuperAdmin = claims?.role === 'super_admin'

  const rows = list.data ?? []

  const onRevokeConfirm = async (): Promise<void> => {
    if (!revoking) return
    const ok = await revoke.mutate(() =>
      api.post(`/v1/clubs/${clubId}/license-keys/${revoking.id}/revoke`)
    )
    if (ok) {
      setRevoking(null)
      list.reload()
    }
  }

  return (
    <section className="detail-block">
      <SectionHead title="Лицензионные ключи" meta={`${rows.length} шт.`} />
      <div className="toolbar toolbar--compact">
        <div className="toolbar__left">
          <span className="muted">{clubName}</span>
        </div>
        <div className="toolbar__right">
          {isSuperAdmin && (
            <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
              {Icon.plus}
              Выпустить ключ
            </Button>
          )}
        </div>
      </div>

      <AsyncState query={list} isEmpty={rows.length === 0} empty="Ключей нет. Выпустите первый." />

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Ключ</th>
                <th>Действует до (лицензия)</th>
                <th>Выпущен</th>
                <th>Активирован</th>
                <th style={{ width: 130 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((k) => (
                <tr key={k.id} style={k.revoked ? { opacity: 0.55 } : undefined}>
                  <td>
                    <code
                      style={{
                        fontFamily: 'monospace',
                        letterSpacing: '0.06em',
                        textDecoration: k.revoked ? 'line-through' : undefined
                      }}
                    >
                      {k.key}
                    </code>
                  </td>
                  <td>{licenseStatusLabel(k.licenseExpiresAt)}</td>
                  <td>{dateOnly(k.createdAt)}</td>
                  <td>
                    {k.activatedAt ? (
                      <span style={{ color: 'var(--green)' }}>{dateOnly(k.activatedAt)}</span>
                    ) : (
                      <span className="muted">не использован</span>
                    )}
                  </td>
                  <td>
                    {k.revoked ? (
                      <span className="status">
                        <span className="dot dot--off" />
                        отозван
                      </span>
                    ) : (
                      isSuperAdmin && (
                        <Button variant="ghost" size="sm" onClick={() => setRevoking(k)}>
                          Отозвать
                        </Button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <LicenseKeyForm
          clubId={clubId}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false)
            list.reload()
          }}
        />
      )}

      {revoking && (
        <ConfirmDialog
          title="Отозвать лицензионный ключ"
          message={
            revoking.activatedAt
              ? `Отозвать ключ ${revoking.key}? Он уже активирован — связанный терминал тоже будет отозван, и выданная лицензия перестанет действовать. Это действие нельзя отменить.`
              : `Отозвать ключ ${revoking.key}? Его больше нельзя будет активировать. Это действие нельзя отменить.`
          }
          confirmLabel="Отозвать"
          busy={revoke.saving}
          error={revoke.error}
          onConfirm={() => void onRevokeConfirm()}
          onCancel={() => setRevoking(null)}
        />
      )}
    </section>
  )
}

function LicenseKeyForm({
  clubId,
  onClose,
  onSaved
}: {
  clubId: string
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const d = new Date()
  d.setFullYear(d.getFullYear() + 1)
  const [expiryDate, setExpiryDate] = useState(d.toISOString().slice(0, 10))
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const onSubmit = async (): Promise<void> => {
    const ts = new Date(expiryDate).getTime()
    if (!ts || isNaN(ts)) return setValidation('Выберите корректную дату.')
    if (ts <= Date.now()) return setValidation('Дата должна быть в будущем.')
    setValidation(null)
    const ok = await save.mutate(() =>
      api.post(`/v1/clubs/${clubId}/license-keys`, { licenseExpiresAt: ts })
    )
    if (ok) onSaved()
  }

  return (
    <Modal
      title="Новый лицензионный ключ"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={save.saving}>
            Отмена
          </Button>
          <Button variant="primary" busy={save.saving} onClick={() => void onSubmit()}>
            Выпустить
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Срок действия лицензии" full>
          <Input
            type="date"
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
            autoFocus
          />
          <span className="field__hint">
            Дата, до которой будет действовать лицензия на терминале после активации этим ключом.
          </span>
        </Field>
      </div>
      {validation && (
        <div className="form-error" style={{ marginTop: 14 }}>
          {validation}
        </div>
      )}
      {save.error && (
        <div className="form-error" style={{ marginTop: 14 }}>
          {save.error}
        </div>
      )}
    </Modal>
  )
}

// ── Терминалы клуба с лицензионным статусом ──
function DevicesLicenseSection({ clubId }: { clubId: string }): React.JSX.Element {
  const list = useFetch<Device[]>(`/v1/clubs/${clubId}/devices`, undefined, [clubId])
  const rows = (list.data ?? []).filter((d) => !d.revoked)

  if (!list.loading && rows.length === 0) return <></>

  // Ближайший срок окончания среди активных терминалов — для заметной сводки.
  const expiries = rows
    .map((d) => d.licenseExpiresAt)
    .filter((v): v is number => typeof v === 'number')
  const soonestExpiry = expiries.length > 0 ? Math.min(...expiries) : null

  return (
    <section className="detail-block">
      <SectionHead title="Активные терминалы" meta={`${rows.length} шт.`} />

      {rows.length > 0 && (
        <div
          className="card"
          style={{
            padding: '12px 16px',
            marginBottom: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 15
          }}
        >
          <span style={{ color: 'var(--text-2)' }}>Лицензия клуба:</span>
          {licenseTermLabel(soonestExpiry)}
        </div>
      )}

      <AsyncState query={list} />

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Терминал</th>
                <th>Действует до (лицензия)</th>
                <th>Продление</th>
                <th>Последняя активность</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id}>
                  <td>{d.name}</td>
                  <td>{licenseTermLabel(d.licenseExpiresAt)}</td>
                  <td>
                    {d.renewalStatus === 'pending' ? (
                      <span style={{ color: 'var(--amber)' }}>Ожидает одобрения</span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>{d.lastSeenAt ? dateOnly(d.lastSeenAt) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
